import type { DimensionModifierValue } from "schemas/generated/fileModifiers";

import React from "react";

import DimensionSelector from "@v2/features/modifiers/base/DimensionSelector";
import { useOptimisticModifierValue } from "@v2/features/modifiers/hooks/useOptimisticModifierValue";
import { useComputedElementDimensions } from "@v2/features/modifiers/store/ModifiersStoreContext";
import { deriveDimensionFromClassName } from "replo-runtime/shared/utils/dimensions";

interface HeightModifierProps {
  onChange: (modifiers: DimensionModifierValue[]) => void;
  /** Options to exclude from the dropdown (e.g., ["full"] to hide Fill option) */
  excludeOptions?: ("auto" | "full" | "fixed")[];
}

const HeightModifier: React.FC<HeightModifierProps> = ({
  onChange,
  excludeOptions,
}) => {
  const computedElementDimensions = useComputedElementDimensions();
  const optimisticHeight = useOptimisticModifierValue({
    modifierType: { type: "dimension", side: "height", value: 0 },
    extractor: (elementDesignation) => {
      // Check className first
      const classDerived = deriveDimensionFromClassName(
        elementDesignation.element.props?.className ?? undefined,
        "height",
      );
      if (classDerived !== null) {
        return classDerived;
      }

      // Then check styleOverrides
      const heightValue = elementDesignation.element.styleOverrides?.height;
      if (heightValue === "auto") {
        return "auto";
      }
      if (heightValue === "100%") {
        return "full";
      }

      return null;
    },
  });

  const currentValue = React.useMemo((): number | "auto" | "full" | null => {
    return optimisticHeight ?? null;
  }, [optimisticHeight]);

  const handleValueChange = (height: number | "auto" | "full" | null) => {
    if (height !== null) {
      onChange([
        {
          type: "dimension" as const,
          side: "height",
          value: height,
        },
      ]);
    }
  };

  return (
    <div className="height-modifier">
      <DimensionSelector
        type="height"
        value={currentValue}
        onSelect={handleValueChange}
        excludeOptions={excludeOptions}
        computedDimension={computedElementDimensions?.height ?? null}
      />
    </div>
  );
};

export default HeightModifier;
